<template>
  <div>
    <div class="col-12 col-md-10 col-lg-8 mb-3 px-0">
      <a-card v-if="isSearchVisible()">
        <span slot="header" v-t="'Components.DataTable.Search'" />
        <a-form-group
          slot="body"
          class="mb-0"
          label-class="d-none d-md-inline-block"
          :label="searchFieldLabel"
        >
          <input
            ref="searchInput"
            v-model="search"
            type="text"
            class="form-control"
          />
        </a-form-group>
        <div class="d-flex gap-2" slot="footer">
          <button
            v-t="'Components.DataTable.DoReset'"
            type="button"
            class="btn btn-pf-secondary"
            @click="reset()"
          />
          <button
            v-t="'Components.DataTable.DoSearch'"
            type="button"
            class="btn btn-pf-primary"
            @click="$emit('search', search)"
          />
        </div>
      </a-card>
    </div>
    <div ref="table" class="tabulator table" :class="'table-' + variant" />
    <div v-if="hasFooterSlot" class="row">
      <div class="col text-end">
        <slot name="footer" />
      </div>
    </div>
    <nav ref="pagination" class="table-pagination pagination" />
  </div>
</template>

<script>
import Card from "+/components/card/Card";
import FormGroup from "+/components/form/formGroup/FormGroup";
import Tabulator from "tabulator-tables";
import { dropdownFormatter } from "./utils/dropdownFormatter";
import { checkboxFormatter } from "./utils/checkboxFormatter";
import { TableVariant } from "./utils/tableTypes";

export default {
  name: "ATable",
  components: {
    "a-card": Card,
    "a-form-group": FormGroup
  },
  props: {
    tableData: {
      type: Array,
      default() {
        return [];
      }
    },
    columns: {
      type: Array,
      default() {
        return [];
      }
    },
    variant: {
      type: String,
      default() {
        return TableVariant.OVERVIEW;
      },
      validator(value) {
        return Object.values(TableVariant).indexOf(value) > -1;
      }
    },
    searchFieldLabel: {
      type: String,
      default() {
        return this.$t("Components.DataTable.Search");
      }
    },
    searchValue: {
      type: String,
      default: ""
    },
    filterColumns: {
      type: Array,
      default: () => []
    },
    filterOnType: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tabulator: null,
      search: this.searchValue
    };
  },
  computed: {
    extendedColumns() {
      // extend columns with action col and disabled sort feature by default
      if (this.columns.some((item) => item.type === "action-col")) {
        return this.addActionCol(this.columns);
      }
      if (this.columns.some((item) => item.type === "checkbox-col")) {
        return this.addCheckboxCol(this.columns);
      }
      return this.columns;
    },
    hasFooterSlot() {
      return !!this.$slots["footer"];
    }
  },
  watch: {
    search() {
      this.filterResults();
    },
    tableData: {
      handler: function (newData) {
        this.tabulator.replaceData(newData);
      },
      deep: true
    },
    columns: {
      handler: function (newData) {
        if (newData.some((item) => item.type === "action-col")) {
          this.tabulator.setColumns(this.addActionCol(newData));
        }
        if (newData.some((item) => item.type === "checkbox-col")) {
          this.tabulator.setColumns(this.addCheckboxCol(newData));
        }
      },
      deep: true
    }
  },
  mounted() {
    this.initTable();
    this.filterResults();
    this.registerEnterEvent();
    this.registerPaginationPageSizeEvent();
  },
  updated() {
    this.tabulator.redraw();
    this.manipulatePaginationLastButton();
  },
  methods: {
    initTable() {
      this.tabulator = new Tabulator(this.$refs.table, {
        pagination: "local",
        paginationSize: 20,
        paginationElement: this.$refs.pagination,
        paginationSizeSelector: [10, 15, 20, 25, 50, 100, 200, 500],
        minHeight: 120,
        resizableColumns: false,
        columnHeaderSortMulti: true,
        columnMinWidth: 120,
        layout: "fitColumns",
        autoResize: true,
        responsiveLayout: "collapse",
        data: this.tableData,
        reactiveData: true,
        columns: this.extendedColumns,
        rowClick: (event, row) => {
          this.$emit("row-click", { event, row });
        }
      });
      this.manipulatePaginationButtons();
    },
    manipulatePaginationButtons() {
      this.manipulatePaginationCommonButton();
      this.manipulatePaginationLastButton();
    },
    manipulatePaginationCommonButton() {
      const firstButton = this.$refs.pagination.querySelector(
        "[data-page='first']"
      );
      firstButton.innerText = 1;

      const prevButton =
        this.$refs.pagination.querySelector("[data-page='prev']");
      prevButton.innerHTML = "<i class='pf-sys-angle-left'></i>";

      const nextButton =
        this.$refs.pagination.querySelector("[data-page='next']");
      nextButton.innerHTML = "<i class='pf-sys-angle-right'></i>";
      const paginationSizeLabel = this.$refs.pagination.querySelector("label");
      paginationSizeLabel.innerText = this.$t(
        "Components.DataTable.Pagination.Data"
      );
    },
    manipulatePaginationLastButton() {
      const lastButton =
        this.$refs.pagination.querySelector("[data-page='last']");
      lastButton.innerText = this.tabulator.getPageMax();
    },
    registerEnterEvent() {
      this.$refs.searchInput.addEventListener("keyup", (event) => {
        if (event.key === "Enter") {
          this.$emit("search", this.search);
        }
      });
    },
    registerPaginationPageSizeEvent() {
      const paginationSizeSelector = this.$refs.pagination.querySelector(
        ".tabulator-page-size"
      );
      paginationSizeSelector.addEventListener("change", () => {
        this.manipulatePaginationLastButton();
      });
    },
    reset() {
      this.search = "";
      this.$emit("search", "");
    },
    isSearchVisible() {
      return this.filterColumns.length !== 0;
    },
    filterResults() {
      if (!this.isSearchVisible()) {
        return;
      }
      if (!this.filterOnType) {
        return;
      }
      const filterLikeConfig = this.filterColumns.map((field) => ({
        field,
        type: "like",
        value: this.search
      }));
      this.tabulator.setFilter([filterLikeConfig]);
    },
    getSelectedRows() {
      return this.tabulator.getSelectedRows();
    },
    getRows() {
      return this.tabulator.getRows();
    },
    disableColumnSort(columns) {
      return columns.map((col) => {
        col["headerSort"] = false;
        return col;
      });
    },
    addActionCol(columns) {
      let withoutActionCol = columns.filter((item) => {
        return item.type !== "action-col";
      });
      return [
        ...withoutActionCol,
        {
          field: "actions",
          width: "1%",
          minWidth: 35,
          widthGrow: false,
          formatter: dropdownFormatter,
          headerSort: false,
          variableHeight: true,
          resizable: false,
          responsive: 0
        }
      ];
    },
    addCheckboxCol(columns) {
      let withoutCheckboxCol = columns.filter((item) => {
        return item.type !== "checkbox-col";
      });
      return [
        {
          field: "checkbox",
          formatter: checkboxFormatter,
          width: "1%",
          headerSort: false
        },
        ...withoutCheckboxCol
      ];
    }
  }
};
</script>

<style lang="scss" src="./scss/table.scss" />
